/**
 * CaptureViewer — full-map survey capture viewer with floating popup.
 *
 * @remarks
 * Layout: map takes full area; CaptureImagePanel overlaid bottom-right.
 * Popup can be toggled — when closed, a small badge reopens it.
 * Clicking a point on the map opens popup and selects that capture.
 *
 * CaptureMapArea is extracted to keep cyclomatic complexity within limits.
 */

import { useState } from 'react';
import { useSurveyCaptures } from '../hooks/useSurveyCaptures';
import type { SurveyCapture, SurveySession } from '../types/survey.types';
import { CaptureImagePanel } from './CaptureImagePanel';
import { CaptureMap } from './CaptureMap';

interface CaptureViewerProps {
  session: SurveySession;
  onClose: () => void;
}

// ── Inner area component — isolates conditional branches ──────────────────────

interface CaptureMapAreaProps {
  isLoading: boolean;
  error: string | null;
  captures: SurveyCapture[];
  sessionId: string;
  selectedIndex: number;
  popupOpen: boolean;
  onSelectIndex: (idx: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onPopupClose: () => void;
  onPopupOpen: () => void;
}

function CaptureMapArea({
  isLoading,
  error,
  captures,
  sessionId,
  selectedIndex,
  popupOpen,
  onSelectIndex,
  onPrev,
  onNext,
  onPopupClose,
  onPopupOpen,
}: CaptureMapAreaProps): JSX.Element {
  if (isLoading) {
    return <div className="sv-viewer-state">Cargando capturas…</div>;
  }
  if (error) {
    return <div className="sv-viewer-state sv-viewer-error">{error}</div>;
  }
  if (captures.length === 0) {
    return <div className="sv-viewer-state">Sin capturas disponibles para esta sesión.</div>;
  }

  const selectedCapture = captures[selectedIndex] ?? null;

  return (
    <>
      <CaptureMap captures={captures} selectedIndex={selectedIndex} onSelectIndex={onSelectIndex} />
      {popupOpen ? (
        <CaptureImagePanel
          capture={selectedCapture}
          sessionId={sessionId}
          index={selectedIndex}
          total={captures.length}
          onPrev={onPrev}
          onNext={onNext}
          onClose={onPopupClose}
        />
      ) : (
        <button
          type="button"
          className="sv-popup-badge"
          onClick={onPopupOpen}
          aria-label="Abrir panel de captura"
        >
          <span className="sv-popup-badge-icon">📷</span>
          <span className="sv-popup-badge-text">
            {selectedIndex + 1} / {captures.length}
          </span>
        </button>
      )}
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function CaptureViewer({ session, onClose }: CaptureViewerProps): JSX.Element {
  const { captures, isLoading, error } = useSurveyCaptures(session.id);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(true);

  function handlePrev(): void {
    setSelectedIndex(i => Math.max(0, i - 1));
    setPopupOpen(true);
  }

  function handleNext(): void {
    setSelectedIndex(i => Math.min(captures.length - 1, i + 1));
    setPopupOpen(true);
  }

  function handleSelectIndex(idx: number): void {
    setSelectedIndex(idx);
    setPopupOpen(true);
  }

  return (
    <div className="sv-viewer">
      {/* Header */}
      <div className="sv-viewer-header">
        <button type="button" className="sv-back-btn" onClick={onClose}>
          ← Volver
        </button>
        <span className="sv-viewer-title">{session.name}</span>
        {!isLoading && !error && captures.length > 0 && (
          <span className="sv-capture-count">{captures.length} capturas</span>
        )}
      </div>

      {/* Map container — fills all remaining space */}
      <div className="sv-map-container">
        <CaptureMapArea
          isLoading={isLoading}
          error={error}
          captures={captures}
          sessionId={session.id}
          selectedIndex={selectedIndex}
          popupOpen={popupOpen}
          onSelectIndex={handleSelectIndex}
          onPrev={handlePrev}
          onNext={handleNext}
          onPopupClose={() => setPopupOpen(false)}
          onPopupOpen={() => setPopupOpen(true)}
        />
      </div>
    </div>
  );
}
